import { useMemo } from 'react';
import { useGetCrmIntegrations } from '@tinct/core-api';
import type { AdsProviderType, ReadCrmIntegration } from '@tinct/core-api-models';
import { useConfig } from '@tinct/core-config';
import { useGetCurrentWorkspaceId } from '@tinct/core-workspace';

export type AdsIntegration = ReadCrmIntegration & {
  id: string;
  providerType: AdsProviderType;
};

/**
 * The workspace's ads integrations (Google Ads, LinkedIn Ads) behind their
 * feature flags. `providerByIntegrationId` resolves an ads campaign's
 * `integrationId` to its provider for display; with neither flag on nothing
 * is fetched and every lookup degrades to "unknown".
 */
export function useAdsIntegrations({ readyOnly = true }: { readyOnly?: boolean } = {}) {
  const workspaceId = useGetCurrentWorkspaceId();
  const { isFeatureEnabled } = useConfig();
  const googleAdsEnabled = isFeatureEnabled('googleAdsIntegration');
  const linkedinAdsEnabled = isFeatureEnabled('linkedinAdsIntegration');
  const anyAdsEnabled = googleAdsEnabled || linkedinAdsEnabled;

  const { data, isLoading } = useGetCrmIntegrations(
    { offset: 0, limit: 50, filters: { organizationId: workspaceId ?? '' } },
    { enabled: !!workspaceId && anyAdsEnabled },
  );

  const integrations = useMemo<AdsIntegration[]>(
    () =>
      (data?.data ?? []).filter((i): i is AdsIntegration => {
        if (!i.id) return false;
        if (readyOnly && i.status !== 'ready') return false;
        if (i.providerType === 'google_ads') return googleAdsEnabled;
        if (i.providerType === 'linkedin_ads') return linkedinAdsEnabled;
        return false;
      }),
    [data, readyOnly, googleAdsEnabled, linkedinAdsEnabled],
  );

  const providerByIntegrationId = useMemo(
    () => new Map(integrations.map((i) => [i.id, i.providerType])),
    [integrations],
  );

  return {
    integrations,
    providerByIntegrationId,
    googleAds: integrations.find((i) => i.providerType === 'google_ads'),
    linkedinAds: integrations.find((i) => i.providerType === 'linkedin_ads'),
    googleAdsEnabled,
    linkedinAdsEnabled,
    anyAdsEnabled,
    isLoading,
  };
}

/** Settings route of an ads integration's detail page. */
export const getAdsIntegrationPath = (
  workspaceUrlPrefix: string,
  integration: Pick<AdsIntegration, 'id' | 'providerType'>,
) =>
  `${workspaceUrlPrefix}/settings/integrations/${
    integration.providerType === 'linkedin_ads' ? 'linkedin-ads' : 'google-ads'
  }/${integration.id}`;
